import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { BillingProvider } from "./context/BillingContext";
import { Toaster } from "sonner";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Musings from "./pages/Musings";
import ContentStudio from "./pages/ContentStudio";
import ContentEditor from "./pages/ContentEditor";
import Prompts from "./pages/Prompts";
import Team from "./pages/Team";
import Customers from "./pages/Customers";
import Schedule from "./pages/Schedule";
import Billing from "./pages/Billing";
import Seats from "./pages/Seats";
import AcceptInvite from "./pages/AcceptInvite";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentResult";
import OwnerDashboard from "./pages/owner/OwnerDashboard";
import PulseShare from "./pages/PulseShare";
import Landing from "./pages/Landing";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import { ViziWidget } from "./components/ViziWidget";
import UpgradeModal from "./components/UpgradeModal";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm tracking-[0.25em] uppercase">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AuthRedirect><Login /></AuthRedirect>} />
      <Route path="/register" element={<AuthRedirect><Register /></AuthRedirect>} />
      <Route path="/invite/:token" element={<AuthRedirect><AcceptInvite /></AuthRedirect>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/musings" element={<Protected><Musings /></Protected>} />
      <Route path="/musings/new" element={<Protected><Musings /></Protected>} />
      <Route path="/content" element={<Protected><ContentStudio /></Protected>} />
      <Route path="/content/:id" element={<Protected><ContentEditor /></Protected>} />
      <Route path="/prompts" element={<Protected><Prompts /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/schedule" element={<Protected><Schedule /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/seats" element={<Protected><Seats /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/admin/feedback" element={<Navigate to="/owner?tab=feedback" replace />} />
      <Route path="/owner" element={<Protected><OwnerDashboard /></Protected>} />
      <Route path="/pulse/share/:token" element={<PulseShare />} />
      <Route path="/forgot-password" element={<AuthRedirect><ForgotPassword /></AuthRedirect>} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/" element={<AuthRedirect><Landing /></AuthRedirect>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <BillingProvider>
              <AppRoutes />
              <ViziWidget />
              <UpgradeModal />
              <Toaster position="top-right" richColors />
            </BillingProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
